import { BillingTier } from '../../../core/models/Company'
import { normalizeBillingTier } from '../types'

/**
 * The company form keys that set a company's plan and limits. Only a platform admin may change
 * them (cloud `updateCompanySettings` refuses a changed value from anyone else).
 */
export const COMPANY_BILLING_KEYS = ['billing_tier', 'max_users', 'max_rooms', 'features'] as const

type BillingKey = (typeof COMPANY_BILLING_KEYS)[number]

export function withoutBillingKeys<T extends object>(form: T): T {
  const rest = { ...form } as Record<string, unknown>
  for (const key of COMPANY_BILLING_KEYS) delete rest[key]
  return rest as T
}

/**
 * What the edit form shows for these keys when it is opened for `company`, fallbacks included.
 * The fallbacks are not stored values, so a form that still holds them has not changed.
 */
export function billingFormValues(company: {
  max_users?: number
  max_rooms?: number
  billing_tier?: string
  features?: unknown
}) {
  return {
    max_users: company.max_users || 100,
    max_rooms: company.max_rooms || 50,
    billing_tier:
      (normalizeBillingTier(company.billing_tier as string) as BillingTier) || BillingTier.STARTER,
    features: company.features || {
      mapEditor: true,
      chat: true,
      whiteboard: true,
      screenSharing: true,
      videoCalling: true,
      customBranding: true,
      advancedAnalytics: false,
      apiAccess: false,
    },
  }
}

const sameValue = (a: unknown, b: unknown): boolean => {
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const left = a as Record<string, unknown>
    const right = b as Record<string, unknown>
    const keys = new Set([...Object.keys(left), ...Object.keys(right)])
    return [...keys].every((key) => left[key] === right[key])
  }
  return a === b
}

/** The billing keys the person changed in the form, compared with what it was opened with. */
export function changedBillingKeys(
  form: Partial<Record<BillingKey, unknown>>,
  company: Parameters<typeof billingFormValues>[0]
): BillingKey[] {
  const opened = billingFormValues(company)
  return COMPANY_BILLING_KEYS.filter((key) => !sameValue(form[key], opened[key]))
}
